import { Link } from 'react-router-dom';

const sectors = [
  {
    title: 'Premium Real Estate',
    subtitle: 'Strategic property investments',
    description: 'Prime commercial and residential properties across Europe and North America. Urban developments, logistics hubs, and undervalued assets with exceptional long-term growth potential.',
    stats: '€1.1B Portfolio · 32 Properties · 94% Occupancy',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20modern%20glass%20office%20building%20facade%20with%20warm%20golden%20sunset%20light%2C%20reflective%20windows%2C%20premium%20architectural%20photography%2C%20clean%20professional%20aesthetic%2C%20cream%20and%20warm%20amber%20tones%2C%20bright%20and%20airy%20atmosphere%2C%20commercial%20real%20estate%20investment%20portfolio&width=800&height=560&seq=sector-realestate-001&orientation=landscape',
    icon: 'ri-building-4-line',
    color: 'accent',
  },
  {
    title: 'Renewable Energy',
    subtitle: 'Clean power infrastructure',
    description: 'Utility-scale solar farms, wind parks, and battery storage facilities powering the transition to a zero-carbon future. Our projects generate clean electricity for over 2 million households annually.',
    stats: '1.8 GW Installed · 15 Sites · 3.2M Tons CO₂ Avoided',
    image: 'https://readdy.ai/api/search-image?query=Expansive%20solar%20panel%20farm%20at%20sunset%20with%20warm%20golden%20light%2C%20rows%20of%20photovoltaic%20panels%2C%20clean%20energy%20infrastructure%2C%20green%20fields%20surrounding%2C%20professional%20editorial%20photography%2C%20bright%20and%20airy%2C%20sustainability%20theme%2C%20modern%20renewable%20energy&width=800&height=560&seq=sector-energy-002&orientation=landscape',
    icon: 'ri-sun-line',
    color: 'primary',
  },
  {
    title: 'Oil & Gas',
    subtitle: 'Strategic energy resources',
    description: 'Selective investments in midstream and downstream oil & gas assets with strong cash flows and established infrastructure. Balanced approach to traditional energy within a diversified portfolio framework.',
    stats: '8 Strategic Assets · 4 Countries · €420M Deployed',
    image: 'https://readdy.ai/api/search-image?query=Modern%20industrial%20oil%20refinery%20at%20dusk%20with%20dramatic%20sky%2C%20clean%20industrial%20aesthetic%2C%20metallic%20structures%20and%20pipelines%2C%20warm%20amber%20and%20steel%20tones%2C%20professional%20architectural%20photography%2C%20energy%20infrastructure%2C%20bright%20sky%20with%20soft%20clouds&width=800&height=560&seq=sector-oilgas-003&orientation=landscape',
    icon: 'ri-oil-line',
    color: 'secondary',
  },
  {
    title: 'Crypto & Digital Assets',
    subtitle: 'Blockchain-native investments',
    description: 'Strategic exposure to digital assets, blockchain infrastructure, and Web3 ventures. Our crypto-friendly approach captures asymmetric upside while maintaining institutional risk management frameworks.',
    stats: '12 Portfolio Companies · €180M Deployed · 5 Protocols',
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20digital%20finance%20abstract%20visualization%20with%20warm%20golden%20holographic%20elements%2C%20blockchain%20network%20nodes%2C%20clean%20minimalist%20design%2C%20premium%20aesthetic%2C%20golden%20and%20cream%20tones%2C%20digital%20asset%20investment%20theme%2C%20bright%20and%20professional%20atmosphere&width=800&height=560&seq=sector-crypto-004&orientation=landscape',
    icon: 'ri-bit-coin-line',
    color: 'accent',
  },
  {
    title: 'Medical Equipment',
    subtitle: 'Healthcare technology',
    description: 'Investments in modern medical device manufacturing, diagnostic equipment, and healthcare technology companies. Supporting innovation that improves patient outcomes and expands access to quality care worldwide.',
    stats: '9 Portfolio Companies · €340M Deployed · 22 Markets',
    image: 'https://readdy.ai/api/search-image?query=Modern%20surgical%20operating%20room%20with%20advanced%20medical%20equipment%20and%20monitoring%20devices%2C%20clean%20sterile%20environment%2C%20professional%20healthcare%20setting%2C%20warm%20clinical%20lighting%2C%20premium%20medical%20technology%2C%20bright%20and%20pristine%20atmosphere%2C%20editorial%20style%20photography&width=800&height=560&seq=sector-medtech-005&orientation=landscape',
    icon: 'ri-heart-pulse-line',
    color: 'primary',
  },
];

export default function SectorsPreviewSection() {
  return (
    <section className="relative py-20 md:py-28 bg-white">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
            Investment Sectors
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 mt-4 mb-5 tracking-tight">
            Diversified Across<br />Five Pillars
          </h2>
          <p className="text-foreground-500 text-base max-w-xl mx-auto leading-relaxed">
            Our multi-sector strategy balances stable cash flows from traditional assets with asymmetric upside from emerging technologies — all managed with UK institutional discipline.
          </p>
        </div>

        {/* Sector Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* First card spans full on some views, then grid */}
          {sectors.map((sector, i) => (
            <div
              key={sector.title}
              className={`group relative bg-background-50 rounded-2xl overflow-hidden border border-foreground-100/30 hover:border-primary-300/50 transition-all duration-500 cursor-pointer ${
                i === 0 ? 'lg:col-span-2 lg:row-span-1' : ''
              }`}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={sector.image}
                  alt={sector.title}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                {/* Icon badge */}
                <div className="absolute top-4 left-4 w-10 h-10 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
                  <i className={`${sector.icon} text-lg text-primary-600`} />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <span className="text-[10px] uppercase tracking-[2px] text-primary-500 font-semibold">
                  {sector.subtitle}
                </span>
                <h3 className="text-xl font-heading font-bold text-foreground-950 mt-1.5 mb-3">
                  {sector.title}
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                  {sector.description}
                </p>
                <div className="flex items-center gap-2 pt-4 border-t border-foreground-100/30">
                  <span className="text-xs text-foreground-400 font-medium">{sector.stats}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link
            to="/sectors"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm uppercase tracking-[2px] font-semibold transition-colors duration-200 cursor-pointer"
          >
            Explore All Sectors
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}